import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Euro, Loader2, Check, ChevronDown } from "lucide-react";
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const COLORS = ["#007AFF", "#34C759", "#FF9F0A", "#AF52DE", "#FF375F", "#5AC8FA", "#FFD60A", "#64D2FF"];
const eur = (v) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);
const fmtMonth = (m) => new Date(m + "-01T00:00:00").toLocaleDateString("fr-FR", { month: "short", year: "2-digit" });

const ClientPicker = ({ clients, selected, toggle }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button data-testid="billing-client-picker" className="flex items-center gap-2 text-sm border border-white/10 rounded-full px-4 py-1.5 text-zinc-300 hover:border-[#007AFF]/50 transition-colors">
        Clients ({selected.length}) <ChevronDown className="w-3.5 h-3.5" />
      </button>
    </PopoverTrigger>
    <PopoverContent align="end" className="w-72 p-1 bg-[#18181b] border-white/10 max-h-80 overflow-y-auto">
      {clients.length === 0 && <p className="text-zinc-500 text-sm p-3">Aucun client facturé.</p>}
      {clients.map((c) => {
        const on = selected.includes(c.societe_id);
        return (
          <button key={c.societe_id} onClick={() => toggle(c.societe_id)} data-testid={`billing-client-option-${c.societe_id}`}
            className="w-full flex items-center gap-2 px-3 py-2 rounded text-left text-sm hover:bg-white/5 transition-colors">
            <span className={`w-4 h-4 rounded border flex items-center justify-center ${on ? "bg-[#007AFF] border-[#007AFF]" : "border-white/20"}`}>{on && <Check className="w-3 h-3 text-white" />}</span>
            <span className="flex-1 truncate text-zinc-200">{c.nom}</span>
            <span className="text-xs text-zinc-500 font-code">{eur(c.total)}</span>
          </button>
        );
      })}
    </PopoverContent>
  </Popover>
);

export const BillingChart = () => {
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState([]);
  const [showGlobal, setShowGlobal] = useState(true);

  useEffect(() => {
    api.get("/dashboard/facturation-clients").then(({ data }) => {
      setData(data);
      setSelected(data.clients.filter((c) => c.total > 0).slice(0, 3).map((c) => c.societe_id));
    }).catch(() => setData({ months: [], global: [], total: 0, clients: [] }));
  }, []);

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const series = useMemo(() => (data?.clients || []).filter((c) => selected.includes(c.societe_id)), [data, selected]);
  const rows = useMemo(() => (data?.months || []).map((m, i) => {
    const r = { month: m, global: data.global[i] };
    series.forEach((c) => { r[c.societe_id] = c.montants[i]; });
    return r;
  }), [data, series]);
  const names = useMemo(() => Object.fromEntries((data?.clients || []).map((c) => [c.societe_id, c.nom])), [data]);

  return (
    <div className="mb-8" data-testid="billing-clients-section">
      <div className="flex items-center gap-2 mb-4">
        <Euro className="w-4 h-4 text-[#FFB020]" />
        <h2 className="font-display text-lg font-medium">Facturation clients</h2>
        {!data && <Loader2 className="w-4 h-4 text-zinc-500 animate-spin" />}
      </div>
      <div className="border border-white/10 rounded-lg bg-[#121212] p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div>
            <p className="font-display font-medium">Évolution mensuelle (HT) · 12 derniers mois</p>
            <p className="text-zinc-500 text-xs mt-0.5">Calculé depuis les rapports et les tarifs clients · Total global : <span data-testid="billing-total" className="text-zinc-300">{eur(data?.total)}</span></p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowGlobal((v) => !v)} data-testid="billing-toggle-global"
              className={`text-sm rounded-full px-4 py-1.5 border transition-colors ${showGlobal ? "bg-white/10 border-white/30 text-white" : "border-white/10 text-zinc-500"}`}>Global</button>
            {data && <ClientPicker clients={data.clients} selected={selected} toggle={toggle} />}
          </div>
        </div>
        {data && data.total === 0 && (
          <p data-testid="billing-no-tarifs" className="text-xs text-[#FFB020] mb-3">Aucun montant calculé : renseignez les tarifs (intervention, ronde, gardiennage) dans les fiches clients.</p>
        )}
        <div className="h-72" data-testid="billing-chart">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rows} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="month" tickFormatter={fmtMonth} stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} tickFormatter={(v) => `${Math.round(v / 1000) >= 1 ? `${Math.round(v / 1000)}k` : v}`} />
              <Tooltip labelFormatter={fmtMonth} formatter={(v, n) => [eur(v), n === "global" ? "Global" : names[n] || n]}
                contentStyle={{ background: "#18181b", border: "1px solid #ffffff20", borderRadius: 8, fontSize: 12 }} />
              <Legend formatter={(n) => (n === "global" ? "Global (tous clients)" : names[n] || n)} wrapperStyle={{ fontSize: 12 }} />
              {showGlobal && <Line type="monotone" dataKey="global" stroke="#e4e4e7" strokeWidth={2.5} strokeDasharray="6 3" dot={false} />}
              {series.map((c, i) => (
                <Line key={c.societe_id} type="monotone" dataKey={c.societe_id} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={{ r: 2.5 }} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};
